import { useEffect, useState } from "react";

import { useAuth } from "../auth";
import { fetchEggBalance } from "./store";
import { readShopCache, cacheShopBalance } from "./local-cache";

// The signed-in player's Golden Egg balance, for anything outside the shop that
// shows it (the golden Shop button in the navbar and on the start page). One
// shared value: every hook instance reads it, and whoever changes the balance
// (a purchase, a spend, a reward) calls setEggBalance or refreshEggBalance so
// they all follow. null = signed out, or not known yet.
let current: number | null = null;
let currentUid: string | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<(v: number | null) => void>();

function publish(v: number | null) {
  current = v;
  listeners.forEach((l) => l(v));
}

// The balance is known to have changed to `v` (the shop just bought or spent).
export function setEggBalance(v: number | null) {
  if (v != null && currentUid && !demo) cacheShopBalance(currentUid, v);
  publish(v);
}

// The balance as last known (null: signed out or not read yet).
export function eggBalanceNow(): number | null {
  return current;
}

// Read the balance afresh (after a reward was granted somewhere else).
export function refreshEggBalance(): Promise<void> {
  if (!currentUid || demo) return Promise.resolve();
  if (inflight) return inflight;
  const uid = currentUid;
  inflight = fetchEggBalance()
    .then((v) => {
      if (uid !== currentUid || v == null) return;
      cacheShopBalance(uid, v);
      publish(v);
    })
    .finally(() => { inflight = null; });
  return inflight;
}

// The design labs: a made-up balance that shows signed out too.
let demo = false;
export function demoEggBalance(v: number): void {
  demo = true;
  currentUid = "demo";
  publish(v);
}

export function useEggBalance(): number | null {
  const { user } = useAuth();
  const uid = demo ? "demo" : user?.id ?? null;
  const [value, setValue] = useState<number | null>(() => (uid && uid === currentUid ? current : uid ? readShopCache(uid)?.balance ?? null : null));
  useEffect(() => {
    listeners.add(setValue);
    return () => { listeners.delete(setValue); };
  }, []);
  useEffect(() => {
    if (demo) { setValue(current); return; }
    if (uid !== currentUid) {
      currentUid = uid;
      // the last known value paints at once, the fresh read corrects it
      publish(uid ? readShopCache(uid)?.balance ?? null : null);
      if (uid) refreshEggBalance();
    } else {
      setValue(current);
    }
  }, [uid]);
  return uid ? value : null;
}
